import { faCircleInfo, faCheck, faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Typography, Spacer, Button, FloatingLabelInput, Arrange } from "@aviary";
import { EmailInput } from "@shared/components/EmailInput/EmailInput";
import { isEmailValid } from "@shared/components/EmailInput/EmailValidation/EmailValidation";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./JourneyAvailabilityForm.styles";

interface Props {
  onContinue?: (email: string, zipCode: string) => void;
  loading?: boolean;
  initialEmail?: string;
  initialZipCode?: string;
}

const JourneyAvailabilityForm = ({
  onContinue,
  loading = false,
  initialEmail = "",
  initialZipCode = "",
}: Props) => {
  const { tablet } = useBreakpoints();
  const { t } = useTranslation();
  const [isEmailDirty, setIsEmailDirty] = useState(false);
  const [email, setEmail] = useState(initialEmail);
  const [zipCode, setZipCode] = useState(initialZipCode);
  const isMobile = tablet.lessThan;

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (!isEmailDirty) {
      setIsEmailDirty(true);
    }
  };

  const handleEmailSet = (value: string) => {
    setEmail(value);
    if (!isEmailDirty) {
      setIsEmailDirty(true);
    }
  };

  const handleZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZipCode(e.target.value);
  };

  const handleContinue = () => {
    if (!email || !zipCode) return;

    onContinue?.(email, zipCode);
  };

  const renderHeader = () => (
    <div>
      {!!isMobile && <Spacer height="oneHalf" />}
      <Typography type="h2">{t(l.journeyCheckout.availability.title)}</Typography>
      <Spacer height="oneHalf" />
    </div>
  );

  const validateEmail = () => {
    if (!isEmailValid(email, new RegExp(""))) {
      return t(l.journeyCheckout.availability.errors.emailInvalid);
    }

    return undefined;
  };

  const renderForm = () => (
    <div>
      <EmailInput
        label={t(l.journeyCheckout.availability.emailLabel)}
        value={email}
        setEmail={handleEmailSet}
        onChange={handleEmailChange}
        isDirty={isEmailDirty}
        isLoading={loading}
        required
        error={validateEmail()}
      />
      <Spacer height="one" />
      <FloatingLabelInput
        id="zipCode"
        type="text"
        label={t(l.journeyCheckout.availability.zipCodeLabel)}
        value={zipCode}
        onChange={handleZipCodeChange}
        required
      />
      <Spacer height="quarter" />
      <Arrange css={styles.availabilityNote}>
        <FontAwesomeIcon icon={faCircleInfo} size="sm" />
        <Typography type="body" isSubdued>
          {t(l.journeyCheckout.availability.locationNote)}
        </Typography>
      </Arrange>
      <Spacer height="oneHalf" />
      <Button
        onClick={handleContinue}
        isFullWidth
        size="large"
        isLoading={loading}
        disabled={!email || !zipCode}
      >
        {t(l.journeyCheckout.availability.continueButton)}&nbsp;
        <FontAwesomeIcon icon={faChevronRight} />
      </Button>
    </div>
  );

  const renderFeature = (text: string, index: number) => (
    <Arrange key={index} css={styles.featureItem}>
      <FontAwesomeIcon icon={faCheck} css={styles.checkIcon} />
      <Typography>{text}</Typography>
    </Arrange>
  );

  const renderJourneysFeatures = () => (
    <div css={styles.container}>
      <Typography type="h3">{t(l.journeyCheckout.availability.uniqueTitle)}</Typography>
      <Spacer height="half" />
      <div>
        {renderFeature(t(l.journeyCheckout.availability.features.trackResults), 0)}
        {renderFeature(t(l.journeyCheckout.availability.features.sharedProvider), 1)}
        {renderFeature(t(l.journeyCheckout.availability.features.personalizedPlan), 2)}
        {renderFeature(t(l.journeyCheckout.availability.features.noVisits), 3)}
      </div>
    </div>
  );

  return (
    <div>
      {renderHeader()}
      {renderForm()}
      <Spacer height="oneHalf" />
      {renderJourneysFeatures()}
    </div>
  );
};

export { JourneyAvailabilityForm };
